import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FlatList, ListRenderItemInfo } from 'react-native';

import Highlighter from '@components/HighlightWords/index';
import { Box, Text } from '@components/Restyle/index';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import Icon from '@components/shared/Icon/Icon';
import SearchInput from '@components/shared/SearchInput/SearchInput.web';
import { SimpleHoverable } from '@components/TaskList/SimpleHoverable';
import {
  ProjectSortOption,
  Project,
  ProjectMemberRole,
} from '@graphql/generated';
import useColors from '@hooks/useColors';
import { useListProjectsFromQuery } from '@hooks/useListProjectsFromQuery';
import Images from '@themes/images';
import theme from '@themes/theme';

interface ProjectSelectV3Props {
  selectedProjectId?: Project['id'];
  onSelectFinish?: (project: Project) => void;
  onClose?: () => void;
}

export const ProjectSelectV3 = ({
  selectedProjectId,
  onSelectFinish,
  onClose,
}: ProjectSelectV3Props) => {
  const listProjectsWithoutSpecifiedRoles = useListProjectsFromQuery({
    first: 25,
    sortBy: ProjectSortOption.NameAsc,
  });

  const { getMappedColor } = useColors();
  const [searchVal, setText] = useState('');
  const {
    projects,
    fetchFromCursor,
    refreshControl,
    refreshing,
    loading,
    pageInfo,
  } = listProjectsWithoutSpecifiedRoles;

  const filteredProject = useMemo(() => {
    return projects.filter((t: Project) => {
      return (
        t.currentUserRole !== ProjectMemberRole.Viewer &&
        (searchVal.length === 0 ||
          t.name.toLowerCase().includes(searchVal.toLowerCase()))
      );
    });
  }, [projects, searchVal]);

  const autoFetchLockRef = useRef(false);

  useEffect(() => {
    if (searchVal.length === 0) {
      autoFetchLockRef.current = false;
      return;
    }

    if (autoFetchLockRef.current) return;

    if (
      filteredProject.length < 6 &&
      !loading &&
      !refreshing &&
      (pageInfo?.hasNextPage ?? false)
    ) {
      autoFetchLockRef.current = true;
      Promise.resolve(fetchFromCursor?.()).finally(() => {
        autoFetchLockRef.current = false;
      });
    }
  }, [
    searchVal,
    filteredProject.length,
    loading,
    refreshing,
    fetchFromCursor,
    pageInfo,
  ]);

  const renderItem = (
    { item }: ListRenderItemInfo<Project>,
    searchVal: string
  ) => {
    return (
      <SimpleHoverable
        accessibilityLabel={`Project | ${item.name}`}
        style={{
          flex: 1,
          cursor: item.id === selectedProjectId ? 'default' : 'pointer',
        }}
        onPress={() => {
          if (item.id === selectedProjectId) return;
          onSelectFinish(item);
          onClose();
        }}>
        {(hover) => (
          <Box mx='m' my='xs' flexDirection='row'>
            <Box
              alignItems='center'
              justifyContent='center'
              width={21}
              height={21}
              borderRadius='xxxs'
              style={{ backgroundColor: getMappedColor(item.color) }}
              mr='xs'>
              <Icon
                color='white'
                variant='xxxs'
                name={
                  item?.icon
                    ? (item?.icon as keyof typeof Images)
                    : 'ProjectIcon'
                }></Icon>
            </Box>
            <Text
              flexShrink={1}
              numberOfLines={1}
              ellipsizeMode='tail'
              variant='webBodySecondary'
              color={item.id === selectedProjectId ? 'grey04' : 'textPrimary'}
              textDecorationLine={
                hover && item.id !== selectedProjectId ? 'underline' : undefined
              }>
              <Highlighter
                autoEscape
                highlightStyle={{
                  color: theme.colors.greenSecondary,
                  backgroundColor: theme.colors.background,
                }}
                searchWords={searchVal?.split(' ') || ''}
                textToHighlight={item.name}
              />
            </Text>
          </Box>
        )}
      </SimpleHoverable>
    );
  };

  return (
    <Box minHeight={176}>
      <Box
        flexDirection='row'
        marginTop='s'
        marginBottom='xs'
        marginHorizontal='m'
        height={40}>
        <SearchInput
          closeIconSize='m'
          onTextChanged={(newText) => setText(newText)}
          value={searchVal}
          autoFocus={true}
        />
      </Box>
      <Box
        height={Math.max(3, Math.min(5, filteredProject.length)) * 37}
        mb='m'
        style={
          {
            overflowY: 'auto',
          } as any
        }>
        <FlatList
          data={filteredProject}
          renderItem={(item) => renderItem(item, searchVal)}
          ListEmptyComponent={
            loading ? (
              <Box
                height={48}
                style={{
                  flex: 1,
                  justifyContent: 'center',
                  alignContent: 'center',
                }}>
                <ActivityIndicatorLoading />
              </Box>
            ) : (
              <Box
                justifyContent='center'
                alignItems='center'
                alignContent='center'
                flexDirection='column'>
                <Text mt='xs' variant='body' textAlign='center' color='grey04'>
                  No projects found.
                </Text>
              </Box>
            )
          }
          keyboardDismissMode='on-drag'
          showsHorizontalScrollIndicator={false}
          showsVerticalScrollIndicator={false}
          refreshControl={refreshControl}
          keyExtractor={(item) => (item?.id ?? '').toString()}
          onEndReached={fetchFromCursor}
          onEndReachedThreshold={0.5}
          refreshing={refreshing}
        />
      </Box>
    </Box>
  );
};
